import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Box, Button, Text } from "@chakra-ui/react";
import DynamicPiano, {
  DynamicPianoHandle,
} from "components/exercises/DynamicPiano";
import { ExerciseDefinition } from "utils/exercises/types";
import { useSound } from "providers/SoundProvider";
import PlayButton from "components/PlayButton";

interface ScaleAscendingDescendingProps {
  exercise: ExerciseDefinition;
  onResult: (isCorrect: boolean) => void;
}

const ScaleAscendingDescending = forwardRef(
  (props: ScaleAscendingDescendingProps, ref) => {
    const { exercise, onResult } = props;
    const pianoRef = useRef<DynamicPianoHandle>(null);
    const { playNote, isAudioPlaying, setIsAudioPlaying } = useSound();
    const [interactionAllowed, setInteractionAllowed] = useState(true); // State to manage interaction with the piano

    // Helper function to play the scale
    const handlePlayScale = async () => {
      if (!isAudioPlaying) {
        if (exercise.data.notes) {
          setIsAudioPlaying(true);

          const notes = exercise.data.notes;
          notes.forEach((note, index) => {
            setTimeout(() => playNote(note), index * 500); // Play notes with delay for a nice sequence
          });
        }

        await new Promise((resolve) => setTimeout(resolve, 3000));
        setIsAudioPlaying(false);
      }
    };

    useEffect(() => {
      setInteractionAllowed(true);
      if (!isAudioPlaying) {
        handlePlayScale(); // Play the scale automatically when the exercise loads
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [exercise.data.notes]);

    useImperativeHandle(ref, () => ({
      checkAnswer: () => localCheckAnswer(),
    }));

    const localCheckAnswer = () => {
      if (pianoRef.current && exercise.data.notes) {
        const userNotes = pianoRef.current.getUserSelectedNotes();
        const sortedUserNotes = userNotes.sort((a, b) => a - b);
        const correctNotes = [...exercise.data.notes].sort((a, b) => a - b); // Clone and sort to prevent mutating original data
        const isCorrect =
          userNotes.length === correctNotes.length &&
          sortedUserNotes.every((note, index) => note === correctNotes[index]);
        onResult(isCorrect); // Send the result to the parent component via onResult
        setInteractionAllowed(false); // Disable interaction after checking the answer
      }
    };

    return (
      <Box textAlign="center" width={"100%"}>
        <Text fontSize="xl" fontWeight={500} mb={4}>
          Listen to the scale and play it on the piano
        </Text>
        <PlayButton label={`Play the Scale`} onClick={handlePlayScale} />
        <Box mb={6}></Box>

        {/* The DynamicPiano component is where the user interacts with the piano */}
        <DynamicPiano
          ref={pianoRef}
          exerciseDefinition={exercise}
          disabled={!interactionAllowed}
          correctNotes={exercise.data.notes || []}
        />
      </Box>
    );
  }
);

export default ScaleAscendingDescending;
